// Lote G (M51): GT 17 (id interno GT-30) desde las inspecciones FIRMADAS de Inspecciones v2, con el mismo cálculo y
// las mismas reglas que el lote A (insp-anexo1.ts: vigencia, Cumple, En proceso, No cumple, Pendiente; "No aplica"
// solo como sugerencia; lo manual nunca se sobrescribe; la fuente es la misma, así "Usar cálculo automático" funciona
// igual). Reglas DECIDIDAS POR CLAUDE, por validar con Kevin:
//  - Almacenamiento y manejo: la última inspección firmada y vigente hecha con la plantilla de almacenamiento de
//    productos químicos (categoría "quimicos" de la foto de la plantilla: la del sistema o una copia), contando todas
//    sus preguntas que se pueden evaluar.
//  - Transporte: la última inspección firmada y vigente con preguntas vinculadas a GT 17 (plantilla de la biblioteca
//    "Transporte de productos químicos peligrosos", M51).
//  - Si la empresa transporta (Perfil SSO) o no lo ha respondido: cumple solo si las dos partes cumplen; el estado es el
//    peor de las dos (No cumple > En proceso > Pendiente > Cumple). Si no transporta: cuenta solo el almacenamiento y el
//    manejo, con la razón visible. Una parte con todas sus respuestas en "No aplica" no cuenta; si ninguna cuenta, el
//    cálculo sugiere No aplica.
//  - La norma se cita sin numerales (NTE INEN 2266:2013).
// Señalización (M51, parte 2): GT 31, 32, 33, 34 y 36 (ids GT-31 a GT-34 y GT-36, iguales al número del Anexo; el
// GT 35 lo cubre la plantilla de condiciones generales del lote A con el id GT-25), conforme la NTE INEN-ISO 3864-1:2013, con
// la última inspección firmada y vigente con preguntas del ítem (plantilla de la biblioteca "Señalización de
// seguridad"): exactamente el cálculo del lote A (resultadoItem de insp-anexo1.ts). Aplican siempre.

import { supabase } from '../../supabaseClient';
import type { Traza } from '../pe/pe-tipos';
import type { EstadoGuardadoChecklist } from '../plan-emergencias-compliance-sync';
import { plural } from '../plurales';
import {
  decisionAnexo1, evaluarItem, FUENTE_ANEXO1, FUENTE_INSPECCIONES_ANEXO1, inicioVigencia, presentacionAnexo1, programacionDe, resultadoItem,
  type EstadoCalculoAnexo1, type EvaluacionItem, type HallAnexo1, type InspAnexo1, type ProgAnexo1, type RespAnexo1,
  type ResultadoItemAnexo1, type TrazaItemAnexo1,
} from './insp-anexo1';
import { fechaEc, isoLocal } from './insp-logica';
import type { FotoPlantilla } from './insp-tipos';

export const ITEM_GT17 = 'GT-30';
export const ITEMS_SENALIZACION = ['GT-31', 'GT-32', 'GT-33', 'GT-34', 'GT-36'] as const;
export const ITEMS_LOTE_G = [ITEM_GT17, ...ITEMS_SENALIZACION] as const;
export const MODULO_SENALIZACION = 'Inspecciones v2 (Señalización)';
export const NORMA_GT17 = 'NTE INEN 2266:2013';
export const MODULO_GT17 = 'Inspecciones v2 (Productos químicos)';
/** Clave interna para evaluar todas las preguntas de una inspección de almacenamiento (no es un ítem del Checklist). */
const ALMACENAMIENTO = '__almacenamiento_quimicos';

export type InspLoteG = Omit<InspAnexo1, 'template_snapshot'> & { template_snapshot: Pick<FotoPlantilla, 'sections' | 'category'> | null };

export const RAZON_NO_TRANSPORTA = 'La empresa declaró en el Perfil SSO que no transporta productos químicos peligrosos: cuenta solo el almacenamiento y el manejo.';
export const AVISO_TRANSPORTE_SIN_RESPONDER = 'Falta responder en el Perfil SSO si la empresa transporta productos químicos peligrosos: mientras tanto se pide también la inspección de transporte.';

const firmada = (i: InspLoteG) => i.status === 'completada' && !!i.signed_at && !!i.performed_date;
const preguntas = (i: InspLoteG) => (i.template_snapshot?.sections ?? []).flatMap(s => s.items ?? []);
const esTransporte = (i: InspLoteG) => preguntas(i).some(p => p.anexo_item === ITEM_GT17);
const esAlmacenamiento = (i: InspLoteG) => i.template_snapshot?.category === 'quimicos' && !esTransporte(i);

/** La inspección de almacenamiento con todas sus preguntas bajo la clave interna (así se reusa evaluarItem). */
function comoAlmacenamiento(i: InspLoteG): InspAnexo1 {
  const sections = (i.template_snapshot?.sections ?? []).map(s => ({ ...s, items: (s.items ?? []).map(p => ({ ...p, anexo_item: ALMACENAMIENTO })) }));
  return { ...i, template_snapshot: { sections } };
}

export type ParteGT17 = 'almacenamiento' | 'transporte';
export interface ResultadoParte {
  parte: ParteGT17;
  estado: EstadoCalculoAnexo1;
  vigente: boolean;
  inspeccionId: string | null;
  texto: string;
  ev: EvaluacionItem | null;
}

const NOMBRE_PARTE: Record<ParteGT17, string> = { almacenamiento: 'Almacenamiento y manejo', transporte: 'Transporte' };
const PLANTILLA_PARTE: Record<ParteGT17, string> = {
  almacenamiento: 'la plantilla "Almacenamiento de productos químicos"',
  transporte: 'la plantilla "Transporte de productos químicos peligrosos"',
};

export function resultadoParte(parte: ParteGT17, inspecciones: InspLoteG[], respuestas: RespAnexo1[], hallazgos: HallAnexo1[],
  programaciones: ProgAnexo1[], hoy: Date = new Date()): ResultadoParte {
  const hoyIso = isoLocal(hoy);
  const candidatas = inspecciones.filter(i => firmada(i) && (parte === 'transporte' ? esTransporte(i) : esAlmacenamiento(i)))
    .sort((a, b) => ((b.performed_date as string).localeCompare(a.performed_date as string)) || ((b.signed_at as string).localeCompare(a.signed_at as string)))
    .map(i => ({ i, ...inicioVigencia(programacionDe(i, programaciones, hoyIso)?.frequency ?? null, hoyIso) }));
  const vigente = candidatas.find(x => (x.i.performed_date as string) >= x.desde);
  if (!vigente) {
    const ultima = candidatas[0];
    const texto = ultima
      ? `la última inspección firmada con ${PLANTILLA_PARTE[parte]} es del ${fechaEc(ultima.i.performed_date)} y ya no está vigente (${ultima.ventana})`
      : `no hay inspecciones firmadas con ${PLANTILLA_PARTE[parte]}`;
    return { parte, estado: 'pendiente', vigente: false, inspeccionId: null, texto, ev: null };
  }
  const item = parte === 'transporte' ? ITEM_GT17 : ALMACENAMIENTO;
  const insp = parte === 'transporte' ? (vigente.i as InspAnexo1) : comoAlmacenamiento(vigente.i);
  const ev = evaluarItem(item, insp, respuestas, hallazgos, hoy);
  const base = `inspección firmada del ${fechaEc(vigente.i.performed_date)} (vigencia: ${vigente.ventana})`;
  const detalle = ev.estado === 'no_aplica' ? 'todas las respuestas están en "No aplica"'
    : ev.estado === 'pendiente' ? 'sin respuestas que se puedan evaluar'
      : ev.estado === 'no_cumple' ? `${plural(ev.vencidas, 'falla', 'fallas')} con el hallazgo vencido o sin hallazgo`
        : ev.estado === 'en_proceso' ? `${plural(ev.abiertas, 'falla', 'fallas')} con el hallazgo abierto dentro de su plazo`
          : ev.resueltas > 0 ? 'las fallas tienen su hallazgo cerrado con evidencia' : 'todas las respuestas cumplen';
  return { parte, estado: ev.estado, vigente: true, inspeccionId: vigente.i.id, texto: `${base}: ${detalle}`, ev };
}

const PESO: Record<EstadoCalculoAnexo1, number> = { no_aplica: 0, cumple: 1, pendiente: 2, en_proceso: 3, no_cumple: 4 };

/** Estado combinado: el peor de las partes que cuentan; "No aplica" solo si ninguna cuenta. */
export function combinarPartes(estados: EstadoCalculoAnexo1[]): EstadoCalculoAnexo1 {
  const cuentan = estados.filter(e => e !== 'no_aplica');
  if (cuentan.length === 0) return 'no_aplica';
  return cuentan.reduce((peor, e) => (PESO[e] > PESO[peor] ? e : peor), 'cumple' as EstadoCalculoAnexo1);
}

const ETIQUETA: Record<EstadoCalculoAnexo1, string> = { cumple: 'Cumple', en_proceso: 'En proceso', no_cumple: 'No cumple', pendiente: 'Pendiente', no_aplica: 'No aplica' };
const estadoTraza = (e: EstadoCalculoAnexo1): Traza['estado'] => (e === 'cumple' ? 'cumple' : e === 'no_cumple' ? 'no_cumple' : 'por_validar');

/** GT 17: almacenamiento y manejo, más el transporte si la empresa transporta (o no lo ha respondido). */
export function resultadoGT17(transporta: boolean | null | undefined, inspecciones: InspLoteG[], respuestas: RespAnexo1[], hallazgos: HallAnexo1[],
  programaciones: ProgAnexo1[], hoy: Date = new Date()): ResultadoItemAnexo1 {
  const conTransporte = transporta !== false;
  const partes = [resultadoParte('almacenamiento', inspecciones, respuestas, hallazgos, programaciones, hoy),
    ...(conTransporte ? [resultadoParte('transporte', inspecciones, respuestas, hallazgos, programaciones, hoy)] : [])];
  const estado = combinarPartes(partes.map(p => p.estado));
  const lineas = partes.map(p => `${NOMBRE_PARTE[p.parte]}: ${p.texto}.`);
  const razon = transporta === false ? RAZON_NO_TRANSPORTA : transporta === true ? 'La empresa transporta productos químicos peligrosos: cumple solo si el almacenamiento y el transporte cumplen.'
    : AVISO_TRANSPORTE_SIN_RESPONDER;
  const cierre = estado === 'no_aplica' ? 'Todas las respuestas están en "No aplica": el cálculo sugiere No aplica; lo confirma el administrador.' : '';
  const resumen = [razon, ...lineas, cierre].filter(Boolean).join(' ');
  const trazas: Traza[] = partes.map(p => ({
    metodo: 'inspecciones_v2', norma: NORMA_GT17,
    articulo_o_tabla: `GT 17: ${NOMBRE_PARTE[p.parte].toLowerCase()}, ${ETIQUETA[p.estado].toLowerCase()}`,
    umbral: p.ev ? p.ev.respondidas : 1, valor: p.ev ? p.ev.respondidas - p.ev.fallas + p.ev.resueltas : 0,
    unidad: p.ev ? 'respuestas que cumplen' : 'inspecciones', estado: estadoTraza(p.estado), fuente_doc: FUENTE_ANEXO1,
    nota: `${NOMBRE_PARTE[p.parte]}: ${p.texto}.`,
  }));
  if (transporta === false) {
    trazas.push({ metodo: 'perfil_sso', norma: NORMA_GT17, articulo_o_tabla: 'GT 17: la empresa no transporta productos químicos peligrosos',
      umbral: null, valor: null, unidad: '', estado: 'cumple', fuente_doc: FUENTE_ANEXO1, nota: RAZON_NO_TRANSPORTA });
  }
  const vigente = partes.some(p => p.vigente);
  return { estado, resumen, trazas, vigente, inspeccionId: partes.find(p => p.inspeccionId)?.inspeccionId ?? null };
}

export interface DatosLoteG {
  inspecciones: InspLoteG[];
  respuestas: RespAnexo1[];
  hallazgos: HallAnexo1[];
  programaciones: ProgAnexo1[];
}

export function resultadosLoteG(transporta: boolean | null | undefined, d: DatosLoteG, hoy: Date = new Date()): Record<string, ResultadoItemAnexo1> {
  return {
    [ITEM_GT17]: resultadoGT17(transporta, d.inspecciones, d.respuestas, d.hallazgos, d.programaciones, hoy),
    ...Object.fromEntries(ITEMS_SENALIZACION.map(item => [item, resultadoItem(item, d.inspecciones as InspAnexo1[], d.respuestas, d.hallazgos, d.programaciones, hoy)])),
  };
}

/**
 * Escribe el estado de GT 17 y de la señalización (GT 31, 32, 33, 34 y 36) con la fuente de Inspecciones (lote A) y devuelve su
 * traza. Mismas decisiones que el lote A: nunca lo manual, nunca sobre "No aplica" y nunca "No aplica".
 */
export async function syncLoteG(companyId: string, transporta: boolean | null | undefined, hoy: Date = new Date()): Promise<Record<string, TrazaItemAnexo1>> {
  if (!companyId) return {};
  const items = [...ITEMS_LOTE_G] as string[];
  const [{ data: insps, error: e1 }, { data: resps, error: e2 }, { data: halls, error: e3 }, { data: progs, error: e4 }, { data: existentes, error: e5 }] = await Promise.all([
    supabase.from('insp_inspections').select('id, status, signed_at, performed_date, schedule_id, template_id, template_snapshot').eq('company_id', companyId),
    supabase.from('insp_answers').select('inspection_id, item_key, value, number_value').eq('company_id', companyId),
    supabase.from('insp_findings').select('inspection_id, item_key, status, due_date').eq('company_id', companyId),
    supabase.from('insp_schedules').select('id, template_id, frequency, active, end_date').eq('company_id', companyId),
    supabase.from('checklist_item_estados').select('item_id, estado, auto_source').eq('company_id', companyId).in('item_id', items),
  ]);
  // Sin M41 en la base (o sin permiso) no se toca nada.
  if (e1 || e2 || e3 || e4) return {};
  const res = resultadosLoteG(transporta, { inspecciones: (insps ?? []) as InspLoteG[], respuestas: (resps ?? []) as RespAnexo1[],
    hallazgos: (halls ?? []) as HallAnexo1[], programaciones: (progs ?? []) as ProgAnexo1[] }, hoy);
  const porItem = new Map(((existentes ?? []) as EstadoGuardadoChecklist[]).map(e => [e.item_id, e]));
  const ahora = new Date().toISOString();
  const filas = items.flatMap(item => {
    const d = decisionAnexo1(porItem.get(item), res[item].estado);
    return d ? [{ company_id: companyId, item_id: item, estado: d, auto_source: FUENTE_INSPECCIONES_ANEXO1, updated_at: ahora }] : [];
  });
  if (filas.length && !e5) {
    const { error } = await supabase.from('checklist_item_estados').upsert(filas, { onConflict: 'company_id,item_id' });
    if (error) console.error('syncLoteG', error);
  }
  // Ítems nuevos: la traza se muestra siempre (dice cómo se cumple).
  return Object.fromEntries(items.map(item => {
    const d = decisionAnexo1(porItem.get(item), res[item].estado);
    const existente = d && !e5 ? { item_id: item, estado: d, auto_source: FUENTE_INSPECCIONES_ANEXO1 } : porItem.get(item);
    return [item, { ...presentacionAnexo1(existente, res[item]), modulo: item === ITEM_GT17 ? MODULO_GT17 : MODULO_SENALIZACION }];
  }));
}
